import * as R from 'remeda';
import { Queue } from '@rossum/api-client/queues';
import {
  IconCheck,
  IconCircles,
  IconFileInvoice,
  IconFocusCentered,
  IconUsers,
} from '@rossum/ui/icons/tabler';
import { useSchema } from '../../../business/schema/useSchema';
import { useQueueAssignedUsersCount } from '../../../business/users/useQueueAssignedUsersCount';
import { useExtensionsFeatureSubscription } from '../../../features/pricing/hooks/useExtensionsFeatureSubscription';
import { getSchemaDatapoints } from '../../../features/schemas/getSchemaDatapoints';
import Stats, { StatsType } from '../components/Stats';
import { getMyExtensionsPathWithQuery } from '../helpers';

type QueueStatsProps = {
  selectedQueue: Queue;
};

// algoritmus
const getNumber = (n: number) =>
  ((
    [
      [0, n.toString()],
      [10000, '>10k'],
      [100000, '>100k'],
      [1000000, '>1M'],
      [10000000, '>10M'],
      [100000000, '>100M'],
    ] as const
  ).find(
    ([lowerLimit]) => n < 10000 || (lowerLimit <= n && n < lowerLimit * 10)
  ) ?? [null, '>1B'])[1];

const getDocumentsCount = (counts: Record<string, number>) =>
  R.pipe(counts, R.omit(['purged', 'split']), R.values(), R.sum(), getNumber);

export const QueueStats = ({ selectedQueue }: QueueStatsProps) => {
  const extensionsSubscription = useExtensionsFeatureSubscription();

  const { data: fields = [] } = useSchema(selectedQueue.schema, {
    select: getSchemaDatapoints,
  });
  const fieldsCount = fields.length;
  const enabledFieldsCount = fields.filter(f => !f.hidden).length;

  const assignedUsersCount = useQueueAssignedUsersCount(selectedQueue);

  const accessRoute = `/queues/${selectedQueue.id}/settings/access`;
  const fieldsRoute = `/queues/${selectedQueue.id}/settings/fields`;

  const stats: StatsType[] = [
    {
      value: assignedUsersCount,
      Icon: IconUsers,
      title: 'containers.settings.stats.queue.assignedUsers' as const,
      type: 'assignedUsers',
      link: accessRoute,
    },
    {
      value: getDocumentsCount(selectedQueue.counts),
      Icon: IconFileInvoice,
      title: 'containers.settings.stats.queue.documents' as const,
      type: 'numberOfDocuments',
      link: `/annotations/${selectedQueue.id}`,
    },
    {
      value: enabledFieldsCount,
      Icon: IconCheck,
      title: 'containers.settings.stats.queue.fieldsEnabled' as const,
      type: 'fieldsEnabled',
      link: fieldsRoute,
    },
    {
      value: fieldsCount,
      Icon: IconFocusCentered,
      title: 'containers.settings.stats.queue.fieldsTotal' as const,
      type: 'fieldsTotal',
      link: fieldsRoute,
    },
    ...(extensionsSubscription.canAccess
      ? [
          {
            value: selectedQueue.hooks.length,
            Icon: IconCircles,
            title: 'containers.settings.stats.queue.extensionsTotal' as const,
            type: 'extensionsTotal',
            link: selectedQueue.hooks.length
              ? getMyExtensionsPathWithQuery({
                  queues: selectedQueue.id.toString(),
                })
              : undefined,
          },
        ]
      : []),
  ];

  return <Stats stats={stats} />;
};
